/**
 * AssistantOverlayHost
 *
 * System-wide contextual assistant popout. Mounted once at the app root
 * (alongside PluginModalHost) so it survives route changes and is reachable
 * from every page via the global "Assistant" page action (rendered in
 * every PageToolbar / CommandPalette, see the registration
 * effect below) or the Ctrl/Cmd+`.` hotkey.
 *
 * Pages can also hand it a question with the window event
 * `neuro:assistant:ask` (see assistant-ask.ts): the panel opens and sends it.
 *
 * Can act on the page, not just talk about it: `ui-action-tools.ts` exposes
 * whatever's in the current page's toolbar / Ctrl+K palette as tools
 * (list + run + navigate), and `curated-backend-tools.ts` adds a small,
 * read-only allowlist of platform-wide knowledge/search tools. The model
 * searches the user's knowledge through those tools when a question needs
 * it — there's no automatic RAG lookup before every message (it piled a
 * "[Relevant knowledge]" block into the transcript on every turn).
 *
 * Built entirely from already-published platform packages:
 *   - @neuro/ui-chat        — <Chat /> (message list, input, streaming, tools UI)
 *   - @neuro/chat-adapter   — createPluginChatAdapter (router + tools)
 *   - @neuro/ai-routing     — ModelRouterClient
 *
 * "What's on screen" (screen-context-summary.ts) reaches the model through
 * a `beforeRouterRequest` hook that prepends it to the user's message. The
 * adapter's `contextProvider` isn't used: its context-change system message
 * carries nothing for a custom context type (just "Context set: Screen."),
 * and since the snapshot changes every turn that notice was added — and
 * shown — on every message. The header shows what the assistant sees
 * instead.
 *
 * The conversation belongs to the signed-in user: it's kept for the tab in
 * sessionStorage (conversation-storage.ts), and dropped — in memory and in
 * storage — when they sign out or someone else signs in.
 *
 * See SYSTEM_CHAT_OVERLAY_PLAN_2026-07-19.md for the original design.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Chat } from '@neuro/ui-chat';
import type { VoiceModeConfig } from '@neuro/ui-chat';
import {
  createPluginChatAdapter,
  createRouterBridge,
  mergeTools,
  ConsoleTelemetrySink,
  HookRegistry,
} from '@neuro/chat-adapter';
import type { IChatAdapter } from '@neuro/chat-adapter';
import { ModelRouterClient } from '@neuro/ai-routing';
import { useAuthStore } from '../../stores/authStore';
import { createSpeechServiceTTSProvider } from '../../lib/speech-tts-provider';
import { useDeviceContext } from '../../providers/DeviceTypeProvider';
import { usePageActionRegistry } from '../../plugins/registries/page-action-registry';
import { useActivePageContext } from '../../plugins/registries/active-page-context-store';
import { useAssistantOverlayStore } from '../../stores/assistantOverlayStore';
import { buildScreenContextPayload, currentPageLabel } from './screen-context-summary';
import { createUiActionTools } from './ui-action-tools';
import { CONFIRMED_BACKEND_TOOLS, loadCuratedBackendTools, urlMentionedByUser } from './curated-backend-tools';
import { createAssistantAnalyticsSink } from './assistant-telemetry-sink';
import { buildAssistantApiClient } from './assistant-api-client';
import { EscalateToChatButton } from './EscalateToChatButton';
import { startPluginScreenContextBridge } from './plugin-screen-context-bridge';
import { listenForAssistantAsks } from './assistant-ask';
import { clearConversation, loadConversation, saveConversation } from './conversation-storage';
import { isToggleHotkey, shouldCloseOnEscape } from './overlay-keyboard';
import { deliverToolCallsAtStreamEnd } from './tool-calls-at-stream-end';
import './AssistantOverlay.css';

const ASSISTANT_ACTION_ID = 'core.assistant.toggle';

const GLOBAL_INSTRUCTIONS =
  'You are a contextual assistant inside the Neuro app. A user message may start with what ' +
  'they are looking at; use it. For questions about their own notes, projects or other data, ' +
  'search with knowledge_search or rag_search. To act on the page, call ui_list_actions first, ' +
  'then ui_run_action; use ui_navigate to take the user somewhere.';

function confirmationText(toolName: string, args: Record<string, unknown>): string {
  if (CONFIRMED_BACKEND_TOOLS.has(toolName) && typeof args.url === 'string') {
    return `Let the assistant open this page?\n\n${args.url}`;
  }
  return `Allow the assistant to run "${toolName}"?\n\n${JSON.stringify(args, null, 2)}`;
}

export function AssistantOverlayHost() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const userKey = useAuthStore((s) => s.userId ?? s.username);
  const { isMobile } = useDeviceContext();

  const isOpen = useAssistantOverlayStore((s) => s.isOpen);
  const width = useAssistantOverlayStore((s) => s.width);
  const includeScreenContext = useAssistantOverlayStore((s) => s.includeScreenContext);
  const conversationScope = useAssistantOverlayStore((s) => s.conversationScope);
  const closeOverlay = useAssistantOverlayStore((s) => s.close);
  const setIncludeScreenContext = useAssistantOverlayStore((s) => s.setIncludeScreenContext);
  const setConversationScope = useAssistantOverlayStore((s) => s.setConversationScope);
  const setWidth = useAssistantOverlayStore((s) => s.setWidth);

  const [adapter, setAdapter] = useState<IChatAdapter | null>(null);
  const [initError, setInitError] = useState<string | null>(null);
  // The user the current adapter (and its saved conversation) belongs to.
  const adapterUserRef = useRef<string | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  // A question handed over by `neuro:assistant:ask`, waiting for the adapter.
  const pendingAskRef = useRef<string | null>(null);
  const [askNonce, setAskNonce] = useState(0);

  // Voice mode: STT defaults to the browser's own SpeechRecognition (no
  // config needed); TTS goes through core's /api/speech and uses the voice
  // the user picked for Neuro in chat (`am_eric` only when they haven't),
  // so Neuro sounds the same everywhere. Both are off until the user clicks
  // the mic/speaker button — enabling this doesn't make the assistant start
  // talking on its own.
  const voiceConfig = useMemo<VoiceModeConfig>(
    () => ({
      ttsProvider: createSpeechServiceTTSProvider({ getToken: () => useAuthStore.getState().token }),
      ttsOptions: { voice: 'am_eric', rate: 1.0 },
      autoSpeak: true,
    }),
    [],
  );

  // The hook and tool executors run outside React's render cycle (called by
  // the adapter on every turn / tool call), so they read live values via
  // ref/getState() rather than closing over render-time props.
  const includeScreenContextRef = useRef(includeScreenContext);
  includeScreenContextRef.current = includeScreenContext;

  const navigate = useNavigate();
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  // ── Listen for any plugin volunteering screen context via
  // ctx.events.publish('screen-context', ...) — see
  // plugin-screen-context-bridge.ts. Passive subscription, no auth/network
  // needed to start it. ──
  useEffect(() => {
    return startPluginScreenContextBridge();
  }, []);

  // ── Register the global "Assistant" page action — no `pageIds` means
  // it matches every page, so this single registration puts it in every
  // PageToolbar, the CommandPalette, and the mobile FAB. ──
  useEffect(() => {
    if (!isAuthenticated) return;
    usePageActionRegistry.getState().registerAction({
      id: ASSISTANT_ACTION_ID,
      pluginId: 'core',
      label: 'Assistant',
      icon: '💬',
      description: 'Open the contextual assistant (Ctrl+.)',
      shortcut: 'Ctrl+.',
      priority: 15,
      placements: ['toolbar', 'command-palette', 'fab'],
      onExecute: () => {
        useAssistantOverlayStore.getState().toggle();
      },
    });
    return () => {
      usePageActionRegistry.getState().unregisterAction(ASSISTANT_ACTION_ID);
    };
  }, [isAuthenticated]);

  // ── Global hotkey: Ctrl/Cmd+. toggles; Escape closes from inside the panel ──
  useEffect(() => {
    if (!isAuthenticated) return;
    function handleKeyDown(e: KeyboardEvent) {
      if (isToggleHotkey(e)) {
        e.preventDefault();
        useAssistantOverlayStore.getState().toggle();
        return;
      }
      if (useAssistantOverlayStore.getState().isOpen && shouldCloseOnEscape(e, panelRef.current)) {
        useAssistantOverlayStore.getState().close();
      }
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isAuthenticated]);

  // ── `neuro:assistant:ask`: open the panel and send the page's question ──
  useEffect(() => {
    if (!isAuthenticated) return;
    return listenForAssistantAsks((prompt) => {
      pendingAskRef.current = prompt;
      setAskNonce((n) => n + 1);
      useAssistantOverlayStore.getState().open();
    });
  }, [isAuthenticated]);

  useEffect(() => {
    const prompt = pendingAskRef.current;
    if (!adapter || !prompt) return;
    pendingAskRef.current = null;
    adapter.sendMessage(prompt).catch((err: unknown) => {
      console.warn('[AssistantOverlay] Failed to send the asked question:', err);
    });
  }, [adapter, askNonce]);

  // ── The conversation is the signed-in user's: sign-out, or someone else
  // signing in on this tab, drops it from memory and from storage. ──
  useEffect(() => {
    const owner = adapterUserRef.current;
    if (!adapter || !owner) return;
    if (isAuthenticated && userKey === owner) return;
    adapterUserRef.current = null;
    adapter.cancelCurrent();
    clearConversation(owner);
    pendingAskRef.current = null;
    setAdapter(null);
    setInitError(null);
    if (!isAuthenticated) useAssistantOverlayStore.getState().close();
  }, [adapter, isAuthenticated, userKey]);

  // ── Keep the conversation for this tab (restored when the adapter is built) ──
  useEffect(() => {
    const owner = adapterUserRef.current;
    if (!adapter || !owner) return;
    return adapter.subscribe((state) => {
      // Mid-stream states are saved once the reply finishes.
      if (state.streaming.active || adapterUserRef.current !== owner) return;
      saveConversation(owner, state);
    });
  }, [adapter]);

  // ── Build the adapter lazily, once per user, the first time the panel opens ──
  useEffect(() => {
    if (!isAuthenticated || !userKey || !isOpen || adapter || initError) return;
    const owner = userKey;
    let cancelled = false;

    async function init() {
      try {
        // Tool calls are handed over at stream end — see
        // tool-calls-at-stream-end.ts (otherwise the answer after a ui_* tool
        // is dropped).
        const router = deliverToolCallsAtStreamEnd(
          createRouterBridge(
            new ModelRouterClient({
              baseUrl: '/api/ai',
              credentials: 'include',
              headers: (): Record<string, string> => {
                const token = useAuthStore.getState().token;
                return token ? { Authorization: `Bearer ${token}` } : {};
              },
            }),
          ),
        );

        // What's on screen reaches the model here, rebuilt fresh every
        // turn (not diffed against the last one) so the model always sees
        // current screen context, not just whatever was true when the
        // conversation started.
        //
        // Prepended into the *user* message's own content, not added as a
        // separate system message. Tried that first (both appended after
        // the user turn, and spliced in immediately before it) — in both
        // positions the model insisted it had no context, even though the
        // debug log confirmed the message was in the request. Most likely
        // cause: many chat templates only honor `messages[0]` as "the"
        // system message. Mutating the user message's own content is immune
        // to that — and safe, since `toRouterMessages()` builds fresh
        // objects, so the prefix never reaches the visible transcript.
        // (The adapter doesn't run this hook for the follow-up request after
        // a tool result; that round has the tool result but not the screen.)
        const hooks = new HookRegistry();
        hooks.on('beforeRouterRequest', (event) => {
          if (!includeScreenContextRef.current) return;
          const payload = buildScreenContextPayload();
          if (!payload) return;
          const messages = event.request.messages;
          for (let i = messages.length - 1; i >= 0; i--) {
            if (messages[i].role !== 'user') continue;
            messages[i].content =
              `[What I'm currently looking at:\n${payload.summary}]\n\n${messages[i].content ?? ''}`;
            break;
          }
        });

        let created: IChatAdapter | null = null;
        const userTexts = () =>
          (created?.getState().messages ?? [])
            .filter((m) => m.role === 'user' && typeof m.content === 'string')
            .map((m) => m.content as string);

        const uiActionTools = createUiActionTools(() => navigateRef.current);
        const curatedBackendTools = await loadCuratedBackendTools(buildAssistantApiClient());

        const newAdapter = await createPluginChatAdapter(
          { pluginId: 'core-assistant', api: buildAssistantApiClient() },
          {
            router,
            // `/chat/tools`'s full list (2000+ tools platform-wide) blows
            // past what any LLM tool-calling API accepts, so we don't use
            // includeBackendTools's automatic fetch-everything path — see
            // curated-backend-tools.ts. Tools are instead: a small curated
            // read-only backend allowlist + the UI-action bridge (list/run
            // page actions, navigate) from ui-action-tools.ts.
            tools: mergeTools(uiActionTools, curatedBackendTools),
            includeBackendTools: false,
            includeRooms: false,
            includeRag: false,
            windowOptions: { maxMessages: 50 },
            systemMessages: {
              identity: 'Neuro Assistant',
              globalInstructions: GLOBAL_INSTRUCTIONS,
            },
            onToolConfirmation: async (toolName, args) => {
              // A page the user named (or a page's own ask) needs no
              // second "are you sure" — see curated-backend-tools.ts.
              if (CONFIRMED_BACKEND_TOOLS.has(toolName) && urlMentionedByUser(args.url, userTexts())) {
                return true;
              }
              return window.confirm(confirmationText(toolName, args));
            },
            // Forward turn/tool/knowledge events into the platform's own
            // analytics (utils/analytics.ts) alongside the console sink —
            // otherwise chat-adapter's default telemetry only reaches a
            // console log and a private in-memory event bus nothing else
            // subscribes to. See assistant-telemetry-sink.ts.
            telemetry: [new ConsoleTelemetrySink(), createAssistantAnalyticsSink()],
            hooks,
          },
        );
        created = newAdapter;
        if (cancelled) return;

        const saved = loadConversation(owner);
        if (saved) newAdapter.loadHistory(saved.messages, saved.sessionId);
        adapterUserRef.current = owner;
        setAdapter(newAdapter);
      } catch (err) {
        if (!cancelled) {
          setInitError((err as Error)?.message ?? 'Failed to start assistant');
        }
      }
    }

    void init();
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, userKey, isOpen, adapter, initError]);

  // ── Conversation scope: 'per-page' resets the conversation whenever the
  // active page changes. This is "start fresh on navigate", not a
  // persisted per-page history — the adapter has no concept of storing
  // and restoring separate message histories per page, only
  // resetConversation() (fresh session, blank messages). ──
  const activePageId = useActivePageContext()?.pageId ?? null;
  const lastPageIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (!adapter || conversationScope !== 'per-page') {
      lastPageIdRef.current = activePageId;
      return;
    }
    if (lastPageIdRef.current !== null && activePageId !== lastPageIdRef.current) {
      adapter.resetConversation();
    }
    lastPageIdRef.current = activePageId;
  }, [activePageId, adapter, conversationScope]);

  if (!isAuthenticated || !isOpen) return null;

  // Re-read on every render; renders follow page changes via activePageId.
  const pageLabel = includeScreenContext && activePageId ? currentPageLabel() : undefined;

  function handleResizeStart(e: React.MouseEvent) {
    e.preventDefault();
    const startX = e.clientX;
    const startWidth = width;
    function onMove(moveEvent: MouseEvent) {
      setWidth(startWidth + (startX - moveEvent.clientX));
    }
    function onUp() {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    }
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }

  return (
    <div
      ref={panelRef}
      className={`assistant-overlay ${isMobile ? 'assistant-overlay--mobile' : 'assistant-overlay--desktop'}`}
      style={isMobile ? undefined : { width }}
      role="complementary"
      aria-label="Assistant"
    >
      {!isMobile && (
        <div
          className="assistant-overlay__resize-handle"
          onMouseDown={handleResizeStart}
          aria-hidden="true"
        />
      )}

      <div className="assistant-overlay__header">
        <span className="assistant-overlay__title">Assistant</span>
        <label className="assistant-overlay__context-toggle">
          <input
            type="checkbox"
            checked={includeScreenContext}
            onChange={(e) => setIncludeScreenContext(e.target.checked)}
          />
          Screen context
        </label>
        <select
          className="assistant-overlay__scope-select"
          value={conversationScope}
          onChange={(e) => setConversationScope(e.target.value as 'global' | 'per-page')}
          title="Whether one conversation follows you across pages, or resets when you navigate"
          aria-label="Conversation scope"
        >
          <option value="global">Global chat</option>
          <option value="per-page">New chat per page</option>
        </select>
        {adapter && <EscalateToChatButton adapter={adapter} />}
        <button
          type="button"
          className="assistant-overlay__close"
          aria-label="Close assistant"
          onClick={closeOverlay}
        >
          ✕
        </button>
      </div>

      {adapter && (
        <div className="assistant-overlay__subheader">
          <span className="assistant-overlay__sees" title={activePageId ?? undefined}>
            {pageLabel ? <>Sees: <strong>{pageLabel}</strong></> : 'Not reading the screen'}
          </span>
          <button
            type="button"
            className="assistant-overlay__new-chat"
            onClick={() => adapter.resetConversation()}
            title="Start a new conversation"
          >
            New chat
          </button>
        </div>
      )}

      <div className="assistant-overlay__body">
        {initError && (
          <div className="assistant-overlay__error" role="alert">
            Failed to start assistant: {initError}
            <button type="button" className="assistant-overlay__retry" onClick={() => setInitError(null)}>
              Retry
            </button>
          </div>
        )}
        {!initError && !adapter && (
          <div className="assistant-overlay__loading">Starting assistant…</div>
        )}
        {adapter && (
          <Chat
            adapter={adapter}
            enableVoice
            voiceConfig={voiceConfig}
            placeholder="Ask about this page, search, or run an action…"
            height="100%"
          />
        )}
      </div>
    </div>
  );
}

export default AssistantOverlayHost;
